import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Youtube, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { ContentContext, DEFAULT_CONTENT } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";
import { Footer } from "@/components/site/Footer";

const TAB_IDS = ["about", "resume", "training", "services", "testimonials", "contact"];

const linkIcon = (url) => {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return Youtube;
  if (u.split("?")[0].endsWith(".pdf")) return FileText;
  return ArrowUpRight;
};

export default function Resources() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [zoom, setZoom] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const openZoom = (f) => {
    setZoom(f);
    setZoomLevel(1);
  };
  const closeZoom = () => {
    setZoom(null);
    setZoomLevel(1);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/content")
      .then(({ data }) => setContent((c) => ({ ...c, ...data })))
      .catch(() => {});
  }, []);

  const res = content.resources;

  return (
    <ContentContext.Provider value={content}>
      <main data-testid="resources-page">
        <header className="sticky top-0 z-50 border-b border-champagne/60 bg-ivory/85 backdrop-blur-md">
          <div className="mx-auto max-w-7xl px-6 md:px-12 h-16 md:h-20 flex items-center gap-6 md:gap-10">
            <Link to="/" data-testid="resources-logo" className="shrink-0 font-serif text-xl md:text-2xl font-semibold tracking-wide text-burgundy">
              {content.header.logoLeft}<span className="text-rose">·</span>{content.header.logoRight}
            </Link>
            <nav className="flex-1 flex items-center gap-1 md:gap-2 overflow-x-auto" data-testid="resources-nav-tabs">
              {TAB_IDS.map((id) => (
                <Link
                  key={id}
                  to={`/#${id}`}
                  data-testid={`resources-tab-${id}`}
                  className="relative whitespace-nowrap px-3 md:px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/60 hover:text-burgundy transition-colors duration-300"
                >
                  {content.header.tabs[id]}
                </Link>
              ))}
              <span
                data-testid="resources-tab-active"
                className="relative whitespace-nowrap px-3 md:px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-burgundy"
              >
                {content.header.tabs.resources || "Resources"}
                <span aria-hidden="true" className="absolute left-3 right-3 bottom-0 h-0.5 bg-burgundy" />
              </span>
            </nav>
          </div>
        </header>

        <section className="mx-auto max-w-5xl px-6 md:px-12 py-6 md:py-8 min-h-[60vh]">
          <Reveal>
            <p className="overline-label" data-testid="resources-kicker">Curated by Chloe</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl font-medium tracking-tight text-burgundy" data-testid="resources-title">
              {res.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-charcoal/80" data-testid="resources-intro">
              {res.intro}
            </p>
          </Reveal>

          {res.links.length > 0 && (
            <div className="mt-16 border-t border-champagne" data-testid="resources-links">
              {res.links.map((l, i) => (
                <Reveal key={i} delay={Math.min(i, 4) * 0.05}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`resource-link-${i}`}
                    className="group flex items-center gap-5 border-b border-champagne px-2 md:px-4 py-7 hover:bg-champagne/10 transition-colors duration-300"
                  >
                    {(() => {
                      const Icon = linkIcon(l.url);
                      return (
                        <span className="shrink-0 border border-champagne p-2.5 text-burgundy/70 group-hover:text-burgundy group-hover:border-burgundy transition-colors duration-300" data-testid={`resource-icon-${i}`}>
                          <Icon size={18} strokeWidth={1.5} />
                        </span>
                      );
                    })()}
                    <div className="flex-1">
                      <h3 className="font-serif text-lg md:text-[22px] font-medium text-burgundy group-hover:italic transition-all duration-300">
                        {l.title}
                      </h3>
                      {l.description && (
                        <p className="mt-1.5 max-w-lg text-xs md:text-[13px] leading-relaxed text-charcoal/70">{l.description}</p>
                      )}
                    </div>
                    <ArrowUpRight size={22} strokeWidth={1.5} className="shrink-0 text-champagne group-hover:text-burgundy group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300" />
                  </a>
                </Reveal>
              ))}
            </div>
          )}

          {res.links.length === 0 && res.graphics.length === 0 && (
            <p className="mt-16 text-charcoal/50" data-testid="resources-empty">
              Resources are on their way — check back soon.
            </p>
          )}

          {res.graphics.length > 0 && (
            <>
              <p className="mt-20 text-[11px] uppercase tracking-[0.25em] text-charcoal/50" data-testid="graphics-hint">
                Click any image to view it full size
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-8" data-testid="resources-gallery">
                {res.graphics.map((f, i) => (
                  <Reveal key={f} delay={(i % 2) * 0.08}>
                    <button
                      onClick={() => openZoom(f)}
                      data-testid={`resource-graphic-${i}`}
                      className="block w-full border border-champagne cursor-zoom-in hover:border-burgundy transition-colors duration-300"
                    >
                      <img
                        src={`${API}/files/graphics/${f}`}
                        alt={`Resource infographic ${i + 1}`}
                        className="w-full h-auto"
                      />
                    </button>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </section>

        {zoom && (
          <div
            className="fixed inset-0 z-[80] bg-charcoal/85 backdrop-blur-sm"
            onClick={closeZoom}
            onWheel={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // 1% per wheel tick (deltaY < 0 => zoom in)
              setZoomLevel((z) => {
                const step = e.deltaY < 0 ? 0.01 : -0.01;
                return Math.min(3, Math.max(1, +(z + step).toFixed(2)));
              });
            }}
            data-testid="graphic-lightbox"
          >
            <div className="absolute top-5 right-6 z-10 flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setZoomLevel((z) => Math.max(1, +(z - 0.05).toFixed(2)))}
                disabled={zoomLevel <= 1}
                data-testid="lightbox-zoom-out"
                aria-label="Zoom out"
                className="text-ivory/80 hover:text-ivory disabled:opacity-30 text-xl font-light w-8 h-8 border border-ivory/30 rounded-full transition-colors"
              >
                −
              </button>
              <span className="text-[11px] uppercase tracking-[0.25em] text-ivory/70 w-14 text-center" data-testid="lightbox-zoom-level">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={() => setZoomLevel((z) => Math.min(3, +(z + 0.05).toFixed(2)))}
                disabled={zoomLevel >= 3}
                data-testid="lightbox-zoom-in"
                aria-label="Zoom in"
                className="text-ivory/80 hover:text-ivory disabled:opacity-30 text-xl font-light w-8 h-8 border border-ivory/30 rounded-full transition-colors"
              >
                +
              </button>
              <button
                onClick={closeZoom}
                data-testid="graphic-lightbox-close"
                className="ml-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory/80 hover:text-ivory transition-colors"
              >
                Close ✕
              </button>
            </div>
            <p className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-ivory/50" data-testid="lightbox-hint">
              Scroll to zoom · 1% per tick
            </p>
            <div className="h-full w-full overflow-auto p-6 pt-20" onClick={closeZoom}>
              <img
                src={`${API}/files/graphics/${zoom}`}
                alt="Infographic full size"
                data-testid="graphic-lightbox-image"
                onClick={(e) => e.stopPropagation()}
                className="mx-auto object-contain border border-champagne bg-ivory"
                style={
                  zoomLevel === 1
                    ? { maxHeight: "85vh", maxWidth: "100%" }
                    : { width: `${zoomLevel * 90}%`, maxWidth: "none", cursor: "grab" }
                }
              />
            </div>
          </div>
        )}

        <Footer />
      </main>
    </ContentContext.Provider>
  );
}
